import * as React from "react";
import {inject, observer} from "mobx-react";
import {throttle} from "OK/modules/throttleModule";
import classNames from "classnames";
import classes from "../Clip.styl";
import {ClipsFullscreenStore} from "../../../../lib/ClipsFullscreenStore";
import {VideoPlayerNode} from "../../../../lib/types";

interface ITimeline {
    duration: number;
    playerContainerElement: React.RefObject<VideoPlayerNode>
    store?: ClipsFullscreenStore;
    isCurrentClip: boolean;
    movieId: string;
}

type TimelineMeta = {
    isTouching: boolean;
    timelineWidth: number;
    timelineWidthInOnePercent: number;
    timelineRect: DOMRect | null;
    timelineNode: HTMLDivElement | null;
}

type Meta = {
    currentPercent: number;
    currentPosition: number;
}

const TIMELINE_SPOTTER_SIZE = {
    full: 24,
    half: 12
};

export const Timeline: React.FC<ITimeline> = inject("store")(observer(
    (props) => {
        const store = props.store!;
        const timelineRef = React.useRef<HTMLDivElement>(null);
        const timelineSpotter = React.useRef<HTMLDivElement>(null);
        const timelineMeta = React.useRef<TimelineMeta>({
            isTouching: false,
            timelineWidth: 0,
            timelineWidthInOnePercent: 0,
            timelineRect: null,
            timelineNode: null,
        });

        const [current, setCurrent] = React.useState<Meta>({
            currentPercent: 0,
            currentPosition: 0
        });

        const _touchStart = React.useCallback((event: TouchEvent | MouseEvent) => {
            event.stopPropagation();
            spotterStart(timelineMeta, timelineRef);
        }, [timelineMeta]);

        // eslint-disable-next-line
        const _touchMove = React.useCallback(throttle((event: TouchEvent | MouseEvent) => {
            event.stopPropagation();
            spotterMove(event, timelineMeta, setCurrent, timelineRef);
        }, 50), [timelineMeta]);

        const _touchEnd = React.useCallback((event: TouchEvent | MouseEvent) => {
            event.stopPropagation();
            spotterEnd(event, timelineMeta, props.playerContainerElement, timelineRef, store);
        }, [timelineMeta, props.playerContainerElement, store]);


        React.useEffect(() => {
            const timelineElement = timelineRef.current;
            const timelineSpotterElement = timelineSpotter.current;

            if (!timelineElement || !timelineSpotterElement) {
                return;
            }

            timelineMeta.current.timelineNode = timelineElement;

            if (store.isDesktop) {
                timelineSpotterElement.addEventListener("mousedown", _touchStart);
                timelineSpotterElement.addEventListener("mousemove", _touchMove);
                timelineSpotterElement.addEventListener("mouseup", _touchEnd);
            } else {
                timelineSpotterElement.addEventListener("touchstart", _touchStart);
                timelineSpotterElement.addEventListener("touchmove", _touchMove);
                timelineSpotterElement.addEventListener("touchend", _touchEnd);
            }

            return () => {
                if (!timelineSpotterElement) {
                    return;
                }

                if (store.isDesktop) {
                    timelineSpotterElement.removeEventListener("mousedown", _touchStart);
                    timelineSpotterElement.removeEventListener("mousemove", _touchMove);
                    timelineSpotterElement.removeEventListener("mouseup", _touchEnd);
                } else {
                    timelineSpotterElement.removeEventListener("touchstart", _touchStart);
                    timelineSpotterElement.removeEventListener("touchmove", _touchMove);
                    timelineSpotterElement.removeEventListener("touchend", _touchEnd);
                }
            };

        }, [store.isDesktop, _touchStart, _touchMove, _touchEnd]);

        React.useEffect(() => {
            if (Number(props.movieId) !== store.currentTimelineProgress.movieId) {
                return;
            }

            if (!timelineRef.current || timelineMeta.current.isTouching || !props.isCurrentClip) {
                return;
            }

            if (store.currentTimelineProgress.duration === 0) {
                return;
            }

            const timelineElement = timelineRef.current;
            if (!timelineElement) {
                return;
            }

            if (!timelineMeta.current.timelineRect) {
                timelineMeta.current.timelineRect = timelineElement.getBoundingClientRect();
            }

            const width = (timelineMeta.current.timelineRect ??
                timelineRef.current.getBoundingClientRect()).width - TIMELINE_SPOTTER_SIZE.half;
            const widthInPercents = width / 100;

            const progress = store.getCurrentClipPosition();

            const currentTime = store.currentTimelineProgress.currentTime;

            const timePercentage = currentTime <= 0 ? 0 : currentTime / (
                store.currentTimelineProgress.duration / 100
            );

            if (progress) {
                if (progress.duration.toFixed(0) === store.currentTimelineProgress.duration.toFixed(0)) {
                    setCurrent({
                        currentPosition: widthInPercents * timePercentage,
                        currentPercent: timePercentage
                    });
                }
            }

            store.saveCurrentClipPosition(timePercentage, widthInPercents * timePercentage);
        },
        [
            store,
            store.currentTimelineProgress.currentTime,
            store.currentTimelineProgress.duration,
            store.currentTimelineProgress.movieId,
            props.isCurrentClip,
            store.isAnimationRunning,
            props.movieId
        ]
        );


        const handleResize = React.useCallback(() => {
            if (!(timelineMeta.current && timelineRef.current)) {
                return;
            }
            timelineMeta.current.timelineRect = timelineRef.current.getBoundingClientRect();
        }, []);


        React.useEffect(() => {
            window.addEventListener("resize", handleResize);
            return () => {
                window.removeEventListener("resize", handleResize);
            };
        }, [handleResize]);


        const timelineCn = classNames(
            classes["timeline"],
            {
                [classes["__bigger"]]: timelineMeta.current.isTouching,
            }
        );

        return (
            <div ref={timelineRef} className={timelineCn}>
                <div ref={timelineSpotter} className={classes["timeline__spotter"]}
                     style={{transform: `translateX(${current.currentPosition}px)`}}
                >
                    <div className={classes["timeline__spotter-inner"]}></div>
                </div>
            </div>
        );
    })
);


function spotterStart(
    timelineMeta: React.RefObject<TimelineMeta>,
    timelineRef: React.RefObject<HTMLDivElement>
) {
    if (!timelineMeta.current || !timelineRef.current) {
        return;
    }

    const timelineElement = timelineRef.current;
    timelineMeta.current.isTouching = true;
    timelineMeta.current.timelineNode = timelineElement;
    timelineMeta.current.timelineRect = timelineElement.getBoundingClientRect();
    const width = timelineMeta.current.timelineRect.width;
    timelineMeta.current.timelineWidth = width;
    timelineMeta.current.timelineWidthInOnePercent = width / 100;
}

function spotterMove(
    event: TouchEvent | MouseEvent,
    timelineMeta: React.RefObject<TimelineMeta>,
    setCurrent:  React.Dispatch<React.SetStateAction<Meta>>,
    timelineRef: React.RefObject<HTMLDivElement>
) {
    if (!timelineMeta.current || !timelineRef.current) {
        return;
    }

    if (!timelineMeta.current.isTouching) {
        return;
    }

    let endX;
    const timelineRect = timelineMeta.current.timelineRect ?? timelineRef.current.getBoundingClientRect();

    if (event instanceof MouseEvent) {
        endX = event.clientX - timelineRect.x;
    } else if (event instanceof TouchEvent) {
        endX = event.changedTouches[0].clientX - timelineRect.x;
    }

    if (!endX) {
        return;
    }


    const width = timelineRect.width - TIMELINE_SPOTTER_SIZE.full;
    const widthInPercents = width / 100;
    const currentPosition = Math.max(0, Math.min(endX, width));
    const currentPercent = Math.min(currentPosition, width) / widthInPercents;

    setCurrent({
        currentPosition,
        currentPercent
    });
}

function spotterEnd(
    event: TouchEvent | MouseEvent,
    timelineMeta: React.RefObject<TimelineMeta>,
    playerContainerElement: React.RefObject<VideoPlayerNode>,
    timelineRef: React.RefObject<HTMLDivElement>,
    store: ClipsFullscreenStore
) {
    if (!timelineMeta.current || !playerContainerElement.current || !playerContainerElement.current.node || !timelineRef.current) {
        return;
    }

    const playerContainer = playerContainerElement.current.node;

    timelineMeta.current.isTouching = false;
    let endX;
    const timelineRect = timelineMeta.current.timelineRect ?? timelineRef.current.getBoundingClientRect();

    if (event instanceof MouseEvent) {
        endX = event.clientX - timelineRect.x;
    } else if (event instanceof TouchEvent) {
        endX = event.changedTouches[0].clientX - timelineRect.x;
    }

    if (!endX) {
        return;
    }

    const width = timelineRect.width - (timelineMeta.current.isTouching ? TIMELINE_SPOTTER_SIZE.full : TIMELINE_SPOTTER_SIZE.half);
    const widthInPercents = width / 100;
    const currentPosition = Math.max(0, Math.min(endX, width));
    const currentPercent = Math.min(currentPosition, width) / widthInPercents;
    const currentTime = (store.currentTimelineProgress.duration / 100) * currentPercent;

    if (playerContainer.call) {
        playerContainer.call("seek", currentTime);
        store.saveCurrentClipPosition(currentTime, currentPosition);
    }
}
